import { CheckCircle2, RefreshCw, AlertTriangle, GitBranch, Server } from "lucide-react";
import { cn } from "@fox1ck-org/ui";

interface FleetSummaryProps {
  totals: {
    total: number;
    healthy: number;
    progressing: number;
    degraded: number;
    outOfSync: number;
  };
  activeFilter: FleetFilter;
  onFilterChange: (filter: FleetFilter) => void;
}

export type FleetFilter = "all" | "healthy" | "progressing" | "degraded" | "outOfSync";

interface Tile {
  filter: FleetFilter;
  label: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  active: string;
  hover: string;
  textActive: string;
}

/**
 * Top-of-dashboard fleet health strip. Each tile is a filter chip — click
 * "Degraded 3" to scope the grid below to just the broken apps.
 */
export function FleetSummary({ totals, activeFilter, onFilterChange }: FleetSummaryProps) {
  const tiles: Tile[] = [
    {
      filter: "all",
      label: "Всего",
      value: totals.total,
      icon: Server,
      active: "border-primary/60 bg-primary/5",
      hover: "hover:border-primary/40",
      textActive: "text-foreground",
    },
    {
      filter: "healthy",
      label: "Исправно",
      value: totals.healthy,
      icon: CheckCircle2,
      active: "border-emerald-500/60 bg-emerald-500/5",
      hover: "hover:border-emerald-500/40",
      textActive: "text-emerald-400",
    },
    {
      filter: "progressing",
      label: "Обновляется",
      value: totals.progressing,
      icon: RefreshCw,
      active: "border-blue-500/60 bg-blue-500/5",
      hover: "hover:border-blue-500/40",
      textActive: "text-blue-400",
    },
    {
      filter: "degraded",
      label: "Деградация",
      value: totals.degraded,
      icon: AlertTriangle,
      active: "border-destructive/60 bg-destructive/5",
      hover: "hover:border-destructive/40",
      textActive: "text-destructive",
    },
    {
      filter: "outOfSync",
      label: "Не синхронизировано",
      value: totals.outOfSync,
      icon: GitBranch,
      active: "border-yellow-500/60 bg-yellow-500/5",
      hover: "hover:border-yellow-500/40",
      textActive: "text-yellow-400",
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {tiles.map((tile) => {
        const Icon = tile.icon;
        const isActive = activeFilter === tile.filter;
        const isEmpty = tile.value === 0 && tile.filter !== "all";
        const isFilterOf = tile.filter !== "all";
        return (
          <button
            key={tile.filter}
            type="button"
            onClick={() => onFilterChange(isActive ? "all" : tile.filter)}
            disabled={isEmpty}
            className={cn(
              "rounded-lg border bg-card px-3 py-2.5 text-left transition-colors",
              isActive ? tile.active : tile.hover,
              isEmpty && "opacity-40 cursor-not-allowed"
            )}
          >
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
              <Icon
                className={cn(
                  "h-3.5 w-3.5",
                  tile.filter === "progressing" && tile.value > 0 && "animate-spin"
                )}
              />
              {tile.label}
            </div>
            <div
              className={cn(
                "text-2xl font-bold mt-1",
                isActive || (isFilterOf && tile.value > 0) ? tile.textActive : "text-foreground"
              )}
            >
              {tile.value}
            </div>
          </button>
        );
      })}
    </div>
  );
}
